import { CommonModule } from '@angular/common';

import { Component, OnDestroy, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';

import { Router } from '@angular/router';

import { AutoCompleteCompleteEvent, AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';

import { ButtonModule } from 'primeng/button';

import { DialogModule } from 'primeng/dialog';

import { InputTextModule } from 'primeng/inputtext';

import { TableLazyLoadEvent, TableModule } from 'primeng/table';

import { TagModule } from 'primeng/tag';

import { ToastModule } from 'primeng/toast';

import { ToolbarModule } from 'primeng/toolbar';

import { CheckboxModule } from 'primeng/checkbox';

import { TabViewModule } from 'primeng/tabview';

import { ProgressBarModule } from 'primeng/progressbar';

import { MessageService } from 'primeng/api';

import { finalize, interval, Subscription, switchMap, takeWhile } from 'rxjs';

import {

    CreateCustomerPortalUserRequest,

    CustomerPortalImportJob,

    CustomerPortalImportPreview,

    CustomerPortalUserService,

    CustomerPortalUserSummary

} from '../services/customer-portal-user.service';

import { ProductService } from '../pages/service/product.service';

import { InMemoryStorageService } from '../services/in-memory-storage.service';



interface M3CustomerOption {

    CUNO?: string;

    CUNM?: string;

    WHLO?: string;

    FACI?: string;

    CUSTOMER?: string;

}



@Component({

    selector: 'app-customer-portal-users',

    standalone: true,

    imports: [

        CommonModule,

        FormsModule,

        TableModule,

        ButtonModule,

        DialogModule,

        InputTextModule,

        TagModule,

        ToastModule,

        ToolbarModule,

        CheckboxModule,

        AutoCompleteModule,

        TabViewModule,

        ProgressBarModule

    ],

    providers: [MessageService],

    templateUrl: './customer-portal-users.component.html',

    styleUrl: './customer-portal-users.component.scss'

})

export class CustomerPortalUsersComponent implements OnInit, OnDestroy {

    users: CustomerPortalUserSummary[] = [];

    loading = false;

    totalRecords = 0;

    page = 0;

    pageSize = 20;



    filterCustomerId = '';

    filterEmail = '';

    filterCompany = '';

    filterDivision = '';



    createDialogVisible = false;

    createSubmitted = false;

    createData: CreateCustomerPortalUserRequest = this.emptyCreateForm();

    mustChangePassword = true;



    customerSearchOptions: M3CustomerOption[] = [];

    selectedM3Customer: M3CustomerOption | null = null;

    scopeCompany = '';

    scopeDivision = '';



    importFile: File | null = null;

    importPreview: CustomerPortalImportPreview | null = null;

    importPreviewLoading = false;

    importJob: CustomerPortalImportJob | null = null;

    importJobLoading = false;

    importDryRun = false;

    private pollSub?: Subscription;



    constructor(

        private customerPortalUserService: CustomerPortalUserService,

        private productService: ProductService,

        private inMemoryStorage: InMemoryStorageService,

        private messageService: MessageService,

        private router: Router

    ) {}



    ngOnInit(): void {

        this.scopeCompany = this.inMemoryStorage.get('company') || this.productService.getCompanyId() || '';

        this.scopeDivision = this.inMemoryStorage.get('division') || this.productService.getDivisionId() || '';

        if (this.scopeCompany && this.scopeDivision) {

            this.productService.setCompanyAndDivision(this.scopeCompany, this.scopeDivision);

        }

        this.loadUsers();

    }



    ngOnDestroy(): void {

        this.pollSub?.unsubscribe();

    }



    goBack(): void {

        this.router.navigate(['/dashboard']);

    }



    loadUsers(): void {

        this.loading = true;

        this.customerPortalUserService

            .list({

                customerId: this.filterCustomerId || undefined,

                email: this.filterEmail || undefined,

                company: this.filterCompany || undefined,

                division: this.filterDivision || undefined,

                page: this.page,

                size: this.pageSize

            })

            .pipe(finalize(() => (this.loading = false)))

            .subscribe({

                next: (page) => {

                    this.users = page.content;

                    this.totalRecords = page.totalElements;

                },

                error: (err) => this.showError('Failed to load customer portal users', err)

            });

    }



    onPage(event: TableLazyLoadEvent): void {

        const first = event.first ?? 0;

        const rows = event.rows ?? this.pageSize;

        this.page = rows > 0 ? first / rows : 0;

        this.pageSize = rows;

        this.loadUsers();

    }



    applyFilters(): void {

        this.page = 0;

        this.loadUsers();

    }



    openCreateDialog(): void {

        this.createData = this.emptyCreateForm();

        this.createData.company = this.scopeCompany;

        this.createData.division = this.scopeDivision;

        this.mustChangePassword = true;

        this.createSubmitted = false;

        this.selectedM3Customer = null;

        this.createDialogVisible = true;

    }



    searchM3Customers(event: AutoCompleteCompleteEvent): void {

        const query = event.query?.trim() ?? '';

        if (query.length < 3) {

            this.customerSearchOptions = [];

            return;

        }

        this.productService.searchCustomerNumbers(query).subscribe({

            next: (res) => {

                this.customerSearchOptions = Array.isArray(res) ? res : res ? [res] : [];

            },

            error: () => {

                this.customerSearchOptions = [];

            }

        });

    }



    onM3CustomerSelect(event: AutoCompleteSelectEvent): void {

        const customer = event.value as M3CustomerOption;

        if (!customer) return;

        this.createData.customerId = customer.CUNO || '';

        this.createData.customerName = customer.CUNM || '';

        this.createData.warehouse = customer.WHLO || '';

        if (this.scopeCompany) this.createData.company = this.scopeCompany;

        if (this.scopeDivision) this.createData.division = this.scopeDivision;

    }



    generateTempPassword(): void {

        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

        let body = '';

        for (let i = 0; i < 10; i++) {

            body += chars.charAt(Math.floor(Math.random() * chars.length));

        }

        this.createData.password = `Temp!${body}aA1`;

    }



    createUser(): void {

        this.createSubmitted = true;

        if (!this.isCreateValid()) return;



        this.createData.mustChangePassword = this.mustChangePassword;

        this.customerPortalUserService.create(this.createData).subscribe({

            next: (msg) => {

                this.messageService.add({ severity: 'success', summary: 'Created', detail: msg || 'User created' });

                this.createDialogVisible = false;

                this.loadUsers();

            },

            error: (err) => this.showError('Create failed', err)

        });

    }



    togglePortal(user: CustomerPortalUserSummary): void {

        const portalEnabled = !user.portalEnabled;

        this.customerPortalUserService.update(user.id, { portalEnabled }).subscribe({

            next: (updated) => {

                user.portalEnabled = updated.portalEnabled;

                user.verify = updated.verify;

                this.messageService.add({

                    severity: 'success',

                    summary: 'Updated',

                    detail: portalEnabled ? 'Portal access enabled' : 'Portal access disabled'

                });

            },

            error: (err) => this.showError('Update failed', err)

        });

    }



    resetPassword(user: CustomerPortalUserSummary): void {

        this.customerPortalUserService.resetPassword(user.id).subscribe({

            next: (msg) =>

                this.messageService.add({

                    severity: 'success',

                    summary: 'Password reset',

                    detail: msg || `Reset email sent to ${user.email}`

                }),

            error: (err) => this.showError('Password reset failed', err)

        });

    }



    downloadTemplate(): void {

        this.customerPortalUserService.downloadImportTemplate().subscribe({

            next: (blob) => this.saveBlob(blob, 'customer-portal-users-template.csv'),

            error: (err) => this.showError('Template download failed', err)

        });

    }



    onImportFileSelected(event: Event): void {

        const input = event.target as HTMLInputElement;

        const file = input.files?.[0] ?? null;

        this.importFile = file;

        this.importPreview = null;

        this.importJob = null;

        if (input) input.value = '';

    }



    previewImport(): void {

        if (!this.importFile) {

            this.messageService.add({ severity: 'warn', summary: 'No file', detail: 'Select a CSV file first' });

            return;

        }

        this.importPreviewLoading = true;

        this.customerPortalUserService

            .previewImport(this.importFile)

            .pipe(finalize(() => (this.importPreviewLoading = false)))

            .subscribe({

                next: (preview) => {

                    this.importPreview = preview;

                },

                error: (err) => this.showError('Preview failed', err)

            });

    }



    startImport(): void {

        if (!this.importFile) {

            this.messageService.add({ severity: 'warn', summary: 'No file', detail: 'Select a CSV file first' });

            return;

        }

        this.importJobLoading = true;

        this.customerPortalUserService

            .startImportJob(this.importFile, this.importDryRun)

            .pipe(finalize(() => (this.importJobLoading = false)))

            .subscribe({

                next: (job) => {

                    this.importJob = job;

                    this.pollImportJob(job.id);

                    this.messageService.add({

                        severity: 'info',

                        summary: 'Import started',

                        detail: `Job #${job.id} is running`

                    });

                },

                error: (err) => this.showError('Import start failed', err)

            });

    }



    downloadImportErrors(): void {

        if (!this.importJob?.id) return;

        this.customerPortalUserService.downloadImportErrors(this.importJob.id).subscribe({

            next: (blob) => this.saveBlob(blob, `import-job-${this.importJob!.id}-errors.csv`),

            error: (err) => this.showError('Error export failed', err)

        });

    }



    importProgressPercent(): number {

        if (!this.importJob?.totalRows) return 0;

        return Math.round((this.importJob.processedRows / this.importJob.totalRows) * 100);

    }



    isImportRunning(): boolean {

        return this.importJob?.status === 'PENDING' || this.importJob?.status === 'RUNNING';

    }



    previewOutcomeSeverity(outcome: string): 'success' | 'warn' | 'danger' | 'info' {

        if (outcome === 'OK') return 'success';

        if (outcome === 'SKIP') return 'warn';

        if (outcome === 'ERROR') return 'danger';

        return 'info';

    }



    private pollImportJob(jobId: number): void {

        this.pollSub?.unsubscribe();

        this.pollSub = interval(3000)

            .pipe(

                switchMap(() => this.customerPortalUserService.getImportJob(jobId)),

                takeWhile((job) => job.status === 'PENDING' || job.status === 'RUNNING', true)

            )

            .subscribe({

                next: (job) => {

                    this.importJob = job;

                    if (job.status === 'COMPLETED') {

                        this.messageService.add({

                            severity: 'success',

                            summary: 'Import complete',

                            detail: `Created ${job.createdCount}, skipped ${job.skippedCount}, errors ${job.errorCount}`

                        });

                        this.loadUsers();

                    } else if (job.status === 'FAILED') {

                        this.showError('Import failed', { message: job.failureMessage || 'Unknown error' });

                    }

                },

                error: (err) => this.showError('Import status failed', err)

            });

    }



    isCreateValid(): boolean {

        const d = this.createData;

        return !!(

            d.customerId?.trim() &&

            d.customerName?.trim() &&

            this.isValidEmail(d.email) &&

            d.password?.trim() &&

            d.company?.trim() &&

            d.division?.trim() &&

            d.warehouse?.trim()

        );

    }



    private isValidEmail(email?: string): boolean {

        if (!email?.trim()) return false;

        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    }



    private emptyCreateForm(): CreateCustomerPortalUserRequest {

        return {

            customerId: '',

            customerName: '',

            email: '',

            password: '',

            company: '',

            division: '',

            warehouse: '',

            phoneNo: ''

        };

    }



    private saveBlob(blob: Blob, filename: string): void {

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');

        a.href = url;

        a.download = filename;

        a.click();

        window.URL.revokeObjectURL(url);

    }



    private showError(summary: string, err: unknown): void {

        const body = (err as { error?: string | { message?: string; code?: string } })?.error;

        let detail = 'Unexpected error';

        if (typeof body === 'string') {

            detail = body;

        } else if (body && typeof body === 'object') {

            const msg = (body as { message?: string }).message;

            const code = (body as { code?: string }).code;

            detail = msg || code || detail;

        } else if ((err as { message?: string })?.message) {

            detail = (err as { message: string }).message;

        }

        if (detail.includes('DUPLICATE_EMAIL')) {

            detail = 'A portal user with this email already exists.';

        } else if (detail.includes('DUPLICATE_CUSTOMER_ID')) {

            detail = 'This customer ID already has a portal account.';

        }

        this.messageService.add({ severity: 'error', summary, detail });

    }

}


